import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import type { Activity } from '@/lib/types';
import { activityTypeMeta } from '../activityMeta';

interface MapPanelProps {
  onCreateClick: () => void;
  activities: Activity[];
  highlightActivityId?: string;
  mobileExpanded?: boolean;
  showCloseButton?: boolean;
  showHeader?: boolean;
  hideHeaderDesktop?: boolean;
  showCreateCta?: boolean;
  isDetailPage?: boolean;
}

const DOLE_CENTER: [number, number] = [47.0925, 5.4895];

export default function MapPanel({
  onCreateClick,
  activities,
  highlightActivityId,
  mobileExpanded = false,
  showCloseButton = true,
  showHeader = true,
  hideHeaderDesktop = false,
  showCreateCta = true,
  isDetailPage = false,
}: MapPanelProps) {

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);

  // Timer utilisé pour éviter que l’aperçu
  // disparaisse pendant le déplacement de la souris
  // du marqueur vers le cadre d’aperçu.
  const closeTimeoutRef = useRef<number | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [previewPosition, setPreviewPosition] = useState<{ left: number; top: number; } | null>(null);

  // État de la modale / carte agrandie
  const [isMapExpanded, setIsMapExpanded] = useState(false);


  useEffect(() => {
    if (!mobileExpanded) {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');

    const syncMobileExpansion = () => {
      setIsMapExpanded(mediaQuery.matches);
    };

    syncMobileExpansion();

    mediaQuery.addEventListener('change', syncMobileExpansion);

    return () => {
      mediaQuery.removeEventListener('change', syncMobileExpansion);
      setIsMapExpanded(false);
    };
  }, [mobileExpanded]);


  // Ouverture depuis le Footer
  useEffect(() => {
    if (location.state?.expandMap) {
      setIsMapExpanded(true);

      window.history.replaceState(
        {},
        '',
        window.location.pathname + window.location.search
      );
    }
  }, [location]);

  // --------------------------------------------------
  // Gestion de la fermeture de l’aperçu
  // --------------------------------------------------

  const cancelClosePreview = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const scheduleClosePreview = useCallback(() => {
    cancelClosePreview();

    closeTimeoutRef.current = window.setTimeout(() => {
      setSelectedActivity(null);
      closeTimeoutRef.current = null;
    }, 500);
  }, [cancelClosePreview]);

  // --------------------------------------------------
  // Initialisation de la carte
  // --------------------------------------------------

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) {
      return;
    }

    const map = L.map(mapContainerRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView(DOLE_CENTER, 13);

    L.tileLayer(
      'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }
    ).addTo(map);

    markersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    const timer = window.setTimeout(() => {
      map.invalidateSize();
    }, 100);

    return () => {
      window.clearTimeout(timer);
      cancelClosePreview();

      map.remove();

      mapRef.current = null;
      markersRef.current = null;
    };
  }, [cancelClosePreview]);

  // --------------------------------------------------
  // Mise à jour des marqueurs
  // --------------------------------------------------

  useEffect(() => {
    const map = mapRef.current;
    const markers = markersRef.current;

    if (!map || !markers) {
      return;
    }

    markers.clearLayers();

    if (isDetailPage && highlightActivityId) {
      const highlightedActivity = activities.find(
        (activity) =>
          activity.id === highlightActivityId &&
          activity.latitude != null &&
          activity.longitude != null
      );

      if (highlightedActivity) {
        map.setView(
          [highlightedActivity.latitude!, highlightedActivity.longitude!],
          12,
          { animate: false }
        );
      }
    }

    activities.forEach((activity) => {
      if (
        activity.latitude == null ||
        activity.longitude == null
      ) {
        return;
      }

      const meta = activityTypeMeta[activity.type];
      const isHighlighted = activity.id === highlightActivityId;

      const icon = L.divIcon({
        className: '',
        html: `
          <div class="
           ${isHighlighted ? 'w-14 h-14 bg-[#8F3827]' : 'w-10 h-10 bg-[#e05a3f]'}
            border-2 border-white
            ${isHighlighted ? 'shadow-xl' : 'shadow-md'}
            flex items-center justify-center
          "
          style="
            transform: rotate(-45deg);
            border-radius: 50% 50% 50% 0;
          "
          >
            <div
              class="flex items-center justify-center w-full h-full"
              style="transform: rotate(45deg);"
            >
              <i
                class="${meta.icon} ${isHighlighted ? 'text-white text-2xl' : 'text-[#fff1dc] text-lg'}"
              ></i>
            </div>
          </div>
        `,
        iconSize: isHighlighted ? [56, 56] : [40, 40],
        iconAnchor: isHighlighted ? [28, 56] : [20, 40],
      });


      function getPreviewPosition(map: L.Map, point: L.Point): { left: number; top: number } {
        const previewWidth = 300;
        const previewHeight = 130;
        const margin = 12;

        const mapWidth = map.getSize().x;
        const mapHeight = map.getSize().y;

        let left = point.x + 20;
        let top = point.y - 20;

        if (left + previewWidth > mapWidth - margin) {
          left = point.x - previewWidth - 20;
        }

        if (left < margin) {
          left = margin;
        }

        if (top + previewHeight > mapHeight - margin) {
          top = mapHeight - previewHeight - margin;
        }

        if (top < margin) {
          top = margin;
        }

        return { left, top };
      }

      const marker = L.marker(
        [activity.latitude, activity.longitude],
        {
          icon,
          zIndexOffset: isHighlighted ? 1000 : 0,
        }
      );

      marker.on('mouseover', () => {



        cancelClosePreview();

        setSelectedActivity(activity);

        const point = map.latLngToContainerPoint([
          activity.latitude!,
          activity.longitude!,
        ]);

        setPreviewPosition(getPreviewPosition(map, point));

        // Le zoom et le déplacement ne concernent
        // que les marqueurs non sélectionnés.
        if (isHighlighted) {
          return;
        }

        const zoom = Math.max(map.getZoom(), 14);

        map.setZoomAround(point, zoom, {
          animate: true,
        });

        const mapSize = map.getSize();

        const dx = point.x - mapSize.x / 2;
        const dy = point.y - mapSize.y / 2;

        const strength = 0.08;

        map.panBy(
          [
            dx * strength,
            dy * strength,
          ],
          {
            animate: true,
            duration: 0.3,
          }
        );
      });


      marker.on('mouseout', () => {
        if (isHighlighted) {
          return;
        }
        scheduleClosePreview();
      });

      marker.addTo(markers);
    });
  }, [activities, cancelClosePreview, scheduleClosePreview, highlightActivityId, isDetailPage]);


  // Ouverture Sur Map pour le
  const openInMaps = useCallback((activity: Activity) => {
    if (activity.latitude == null || activity.longitude == null) return;

    const url = `https://www.google.com/maps/search/?api=1&query=${activity.latitude},${activity.longitude}`;

    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  // --------------------------------------------------
  // Si l'activité sélectionnée n'existe plus
  // --------------------------------------------------

  useEffect(() => {
    if (
      selectedActivity &&
      !activities.some(
        (activity) => activity.id === selectedActivity.id
      )
    ) {
      setSelectedActivity(null);
    }
  }, [activities, selectedActivity]);

  // --------------------------------------------------
  // Gestion de la modale
  // --------------------------------------------------

  useEffect(() => {
    const timers: number[] = [];

    const invalidateMapSize = () => {
      const map = mapRef.current;

      if (!map) {
        return;
      }

      const center = map.getCenter();

      map.invalidateSize({
        pan: false,
        animate: false,
      });

      map.setView(center, map.getZoom(), {
        animate: false,
      });
    };

    // Recalcul après le changement de taille
    timers.push(window.setTimeout(invalidateMapSize, 100));

    // Sécurité si le layout n'est pas encore totalement stabilisé
    timers.push(window.setTimeout(invalidateMapSize, 300));

    if (!isMapExpanded) {
      return () => {
        timers.forEach((timer) => window.clearTimeout(timer));
      };
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMapExpanded(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;

      timers.forEach((timer) => window.clearTimeout(timer));

      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMapExpanded]);
  // --------------------------------------------------
  // Nettoyage du timer
  // --------------------------------------------------

  useEffect(() => {
    return () => {
      cancelClosePreview();
    };
  }, [cancelClosePreview]);

  return (
    <div className="flex flex-col gap-5">
      {/* Carte */}
      <div
        className={
          isMapExpanded
            ? `fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-0
            mt-0 md:mt-16
            ${!isDetailPage ? 'pb-0 md:pb-6' : 'pb-[183px] md:pb-6'}
            md:p-6 `
            : 'bg-white border border-background-200 rounded-lg overflow-hidden'
        }
        role={isMapExpanded ? 'dialog' : undefined}
        aria-modal={isMapExpanded ? true : undefined}
        aria-label={
          isMapExpanded ? 'Carte autour de toi' : undefined
        }
      >
        <div
          className={
            isMapExpanded
              ? 'w-full h-full md:max-w-6xl md:h-[90vh] bg-white md:rounded-xl overflow-hidden flex flex-col shadow-2xl'
              : 'w-full'
          }
        >
          {/* En-tête */}
          {showHeader && (
            <div
              className={`flex items-center justify-between px-4 py-3 border-b border-background-200 shrink-0 ${hideHeaderDesktop ? 'lg:hidden' : ''
                }`}
            >
              {/* Titre */}
              <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                <i className="ri-map-pin-2-line text-primary-500"></i>

                {/* Home : toujours "Autour de toi"
                 Détail : "Autour de toi" mobile, "Lieu" desktop */}
                <span>
                  {isDetailPage ? (
                    <>
                      <span className="lg:hidden">Autour de toi</span>
                      <span className="hidden lg:inline">Lieu</span>
                    </>
                  ) : (
                    'Autour de toi'
                  )}
                </span>
              </h4>

              <div className="flex items-center gap-2">
                {/* Home :
                    Mobile + desktop = Grand Dole
                    
                    Détail :
                    Mobile = Grand Dole
                    Desktop = Agrandir
                */}
                {isDetailPage ? (
                  <span className="flex items-center gap-2">
                    {/* Mobile */}
                    <span className="lg:hidden px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold">
                      Grand Dole
                    </span>

                    {/* Desktop */}
                    {showCloseButton && (
                      <button
                        type="button"
                        onClick={() =>
                          setIsMapExpanded((value) => !value)
                        }
                        className="hidden lg:flex items-center gap-2 h-8 px-2 rounded-md text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                        aria-label={
                          isMapExpanded
                            ? 'Fermer la carte'
                            : 'Agrandir la carte'
                        }
                        title={
                          isMapExpanded
                            ? 'Fermer la carte'
                            : 'Agrandir la carte'
                        }
                      >
                        <span className="text-xs font-semibold">
                          {isMapExpanded ? 'Fermer' : 'Agrandir'}
                        </span>

                        <i
                          className={
                            isMapExpanded
                              ? 'ri-close-line text-lg'
                              : 'ri-fullscreen-line text-lg'
                          }
                        />
                      </button>
                    )}
                  </span>
                ) : (
                  <>
                    {/* Home : Grand Dole toujours visible */}
                    <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold">
                      Grand Dole
                    </span>

                    {/* Home : bouton uniquement desktop */}
                    {showCloseButton && (
                      <button
                        type="button"
                        onClick={() =>
                          setIsMapExpanded((value) => !value)
                        }
                        className="w-8 h-8 items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                        aria-label={
                          isMapExpanded
                            ? 'Fermer la carte'
                            : 'Agrandir la carte'
                        }
                        title={
                          isMapExpanded
                            ? 'Fermer la carte'
                            : 'Agrandir la carte'
                        }
                      >
                        <i
                          className={
                            isMapExpanded
                              ? 'ri-close-line text-lg'
                              : 'ri-fullscreen-line text-lg'
                          }
                        />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Zone Leaflet */}
          <div
            className={
              isMapExpanded
                ? 'relative flex-1 min-h-0'
                : 'relative w-full h-[250px]'
            }
          >
            <div
              ref={mapContainerRef}
              className="w-full h-full"
            />

            {/* Aperçu de l'activité */}
            {selectedActivity && (
              <div
                className="absolute z-[1000]"
                style={{
                  left: previewPosition?.left ?? 0,
                  top: previewPosition?.top ?? 0,
                }}
                onMouseEnter={cancelClosePreview}
                onMouseLeave={scheduleClosePreview}
              >
                <div className="w-[300px] bg-white rounded-xl shadow-lg border border-background-200 p-3">
                  <div className="flex items-start gap-3">
                    {/* Icône */}
                    <div className="w-9 h-9 shrink-0 rounded-full bg-primary-100 flex items-center justify-center">
                      <i
                        className={`${activityTypeMeta[selectedActivity.type].icon} text-accent-800 text-lg`}
                      />
                    </div>

                    {/* Titre et bouton en haut */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="min-w-0 flex-1 text-xs font-semibold text-foreground-950 break-words">
                          {selectedActivity.title}
                        </p>

                        {!(isDetailPage && selectedActivity.id === highlightActivityId) && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              cancelClosePreview();
                              setSelectedActivity(null);
                              setIsMapExpanded(false);

                              navigate(`/activite/${selectedActivity.id}`);
                            }}
                            className="shrink-0 px-3 py-1.5 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 transition-colors"
                          >
                            Voir
                          </button>
                        )}
                      </div>

                      {/* Date et heure */}
                      <p className="mt-1 text-xs text-foreground-600 break-words">
                        {selectedActivity.time}
                      </p>

                      {/* Adresse et distance : retour à la ligne autorisé */}
                      <p className="mt-1 text-xs text-foreground-500 break-words [overflow-wrap:anywhere]">
                        {selectedActivity.location}
                        {selectedActivity.distanceKm > 0 &&
                          ` · ${formatDistance(selectedActivity.distanceKm)}`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* CTA */}
      {showCreateCta && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-5">
          <div className="w-11 h-11 rounded-lg bg-primary-500 text-white flex items-center justify-center mb-3">
            <i className="ri-lightbulb-flash-line text-xl"></i>
          </div>

          <h4 className="font-heading text-lg font-semibold text-foreground-950">
            Tu organises quelque chose ?
          </h4>

          <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">
            Propose une activité, une sortie ou un coup de main.
            En deux minutes, tu trouves des gens près de chez toi.
          </p>

          <button
            type="button"
            onClick={onCreateClick}
            className="mt-4 w-full px-4 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Créer une activité
          </button>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------
// Distance
// --------------------------------------------------

function formatDistance(distanceKm: number): string {
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m`;
  }

  return `${distanceKm.toFixed(1)} km`;
}